/* The card's "Additional fields" (stage 1): the company's own fields, set up in the admin, on the
   Overview — shown in VIEW, edited with the rest of the form in EDIT.

   `custom_fields` on `GET excalibur/counterparties/:id/` is every field (deleted ones are not sent), in order:
   `{id, code, label, type, options, value}`. `type` is text | number | boolean | date | choice;
   `options` (choice only) are `{id, label}`. Options are soft-deleted in the admin: a deleted one
   comes nowhere — not in `options`, not in `value` (a field holding one arrives as null, empty) —
   and picking it is a 400. `value` is a string, a JSON number (6.0 arrives as 6), a boolean,
   "YYYY-MM-DD", `{id, label}`, or null.

   A card PATCH takes `custom_fields: [{field_id, value}]` (JSON only) — partial: only the fields
   sent change, null clears (empty text too). Errors come back per ITEM, in the order sent:
   `{custom_fields: [{}, {value: [...]}]}` — `Page.js` maps the index back to the field.

   The editors follow what the API takes: text ≤ 1000 characters (`CUSTOM_TEXT_MAX_LENGTH`); a
   number of at most 15 digits, 4 after the separator (no `precision`, so nothing is rounded on
   the way); a yes / no Select with a clear button, because a Switch has no third state for "not
   filled in"; the card's own date picker; a choice Select over `options` as they come. */

import React from "react";
import { DatePicker, Input, InputNumber, Select } from "antd";

import { COUNTERPARTIES_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

const CF_TEXT_MAX = 1000;
const NUMBER_MAX = 99999999999.9999;

export const cfFieldId = field => `cp-field-cf-${field.id}`;

/** The value in the shape the API takes and the dirty check compares; "nothing" is always null. */
export function cfWire(field) {
  const value = field.value;
  if (value === null || value === undefined) return null;
  if (field.type === "choice") return value.id ?? null;
  if (field.type === "text") return String(value).trim() ? String(value) : null;
  return value;
}

/** The decimal separator of the interface language, from Intl (`1.5` written out). */
export const decimalSeparator = lang =>
  (1.5)
    .toLocaleString(localeFor(lang))
    .replace(/\d/g, "")
    .charAt(0) || ".";

/** A value as text for VIEW (`null` → the muted "not filled in"). */
export function cfDisplay(field, lang) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const value = field.value;
  if (value === null || value === undefined || value === "") return <span className="cp-cf-empty">{t("cf_empty")}</span>;
  switch (field.type) {
    case "number":
      return <span className="lp-num">{new Intl.NumberFormat(localeFor(lang), { maximumFractionDigits: 4 }).format(Number(value))}</span>;
    case "boolean":
      return t(value ? "cf_yes" : "cf_no");
    case "date":
      return dayjs(value).format("DD.MM.YYYY");
    case "choice":
      return value.label;
    default:
      return <span className="cp-cf-text">{String(value)}</span>;
  }
}

/** The editor of one field, by its type. `onChange` gets the new `value` in the card's own shape. */
export function cfEditor(field, onChange, { lang, status }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const id = cfFieldId(field);
  const value = field.value;
  switch (field.type) {
    case "number":
      return (
        <InputNumber
          id={id}
          status={status}
          style={{ width: "100%" }}
          value={value ?? null}
          max={NUMBER_MAX}
          min={-NUMBER_MAX}
          decimalSeparator={decimalSeparator(lang)}
          onChange={next => onChange(next === null || next === undefined || next === "" ? null : Number(next))}
        />
      );
    case "boolean":
      return (
        <Select
          id={id}
          status={status}
          allowClear
          placeholder={t("cf_empty")}
          value={value === null || value === undefined ? undefined : value}
          options={[
            { value: true, label: t("cf_yes") },
            { value: false, label: t("cf_no") },
          ]}
          onChange={next => onChange(next === undefined ? null : next)}
        />
      );
    case "date":
      return (
        <DatePicker
          id={id}
          status={status}
          style={{ width: "100%" }}
          format="DD.MM.YYYY"
          placeholder={t("cf_empty")}
          value={value ? dayjs(value) : null}
          onChange={next => onChange(next ? next.format("YYYY-MM-DD") : null)}
        />
      );
    case "choice":
      return (
        <Select
          id={id}
          status={status}
          allowClear
          placeholder={t("cf_empty")}
          value={value?.id ?? undefined}
          options={(field.options || []).map(o => ({ value: o.id, label: o.label }))}
          onChange={next => onChange(next === undefined ? null : (field.options || []).find(o => o.id === next) || null)}
        />
      );
    default:
      return <Input id={id} status={status} allowClear maxLength={CF_TEXT_MAX} value={value ?? ""} onChange={e => onChange(e.target.value)} />;
  }
}

/** The hint under an editor, where its type needs one. */
export function cfHint(field, lang) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  if (field.type === "boolean") return t("cf_clear_hint");
  if (field.type === "number") return t("cf_number_hint");
  return undefined;
}
